import { APP_VERSION } from '@/version';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Shield, Settings } from 'lucide-react';

import AdminTabs from '@/components/AdminTabs';
import AdminStatusTab from '@/components/AdminStatusTab';
import AdminSettingsTab from '@/components/AdminSettingsTab';
import AdminReportsTab from '@/components/AdminReportsTab';
import AdminYouTubeLogsTab from '@/components/AdminYouTubeLogsTab';
import AdminImportTab from '@/components/AdminImportTab';
import AdminAdminsTab from '@/components/AdminAdminsTab';
import { Badge } from '@/components/ui/badge';

export default function AdminDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('status');

  // Initialize tab from URL parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['status', 'reports', 'youtube', 'settings', 'import', 'admins'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Update URL when tab changes
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tab);
    setSearchParams(newSearchParams);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'status':   return <AdminStatusTab />;
      case 'reports':  return <AdminReportsTab />;
      case 'youtube':  return <AdminYouTubeLogsTab />;
      case 'settings': return <AdminSettingsTab />;
      case 'import':   return <AdminImportTab />;
      case 'admins':   return <AdminAdminsTab />;
      default:         return <AdminStatusTab />;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Settings className="h-8 w-8" />
              Admin Settings
            </h1>
            <p className="text-muted-foreground">
              System status, reports, YouTube logs, and configuration reference
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="flex items-center gap-1">
              <Shield className="h-3 w-3" />
              Administrator
            </Badge>
            <Badge variant="secondary">v{APP_VERSION}</Badge>
          </div>
        </div>
      </div>

      {/* Tabbed Navigation */}
      <AdminTabs activeTab={activeTab} onTabChange={handleTabChange} />

      {/* Tab Content */}
      <div className="min-h-[600px]">
        {renderTabContent()}
      </div>
    </div>
  );
}
